import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { 
  getChanges, getChange, createChange, updateChange,
  submitChange, approveChange, startChangeImplementation,
  completeChange, addChangeRiskAssessment, addChangeImplementationStep,
  updateChangeImplementationStep, getChangeStats, getUsers
} from '@/services/api';
import { 
  GitBranch, Plus, Search, FileText, AlertTriangle, CheckCircle2,
  XCircle, Clock, Play, Eye, ChevronRight, Loader2, Send,
  Shield, ListTodo, User, Calendar, RefreshCw, Filter
} from 'lucide-react';

const CHANGE_CATEGORIES = [
  { value: 'operational', label: 'Operational', labelAr: 'تشغيلي' },
  { value: 'organizational', label: 'Organizational', labelAr: 'تنظيمي' },
  { value: 'procedural', label: 'Procedural', labelAr: 'إجرائي' },
  { value: 'equipment', label: 'Equipment', labelAr: 'معدات' },
  { value: 'infrastructure', label: 'Infrastructure', labelAr: 'بنية تحتية' },
  { value: 'regulatory', label: 'Regulatory', labelAr: 'تنظيمي' },
  { value: 'technology', label: 'Technology', labelAr: 'تقنية' },
  { value: 'personnel', label: 'Personnel', labelAr: 'موظفين' },
  { value: 'other', label: 'Other', labelAr: 'أخرى' }
];

const STATUS_CONFIG = {
  draft: { label: 'Draft', labelAr: 'مسودة', color: 'bg-slate-600', icon: FileText },
  submitted: { label: 'Submitted', labelAr: 'مقدم', color: 'bg-blue-600', icon: Send },
  under_review: { label: 'Under Review', labelAr: 'قيد المراجعة', color: 'bg-amber-600', icon: Eye },
  risk_assessment: { label: 'Risk Assessment', labelAr: 'تقييم المخاطر', color: 'bg-orange-600', icon: Shield },
  approved: { label: 'Approved', labelAr: 'معتمد', color: 'bg-emerald-600', icon: CheckCircle2 },
  rejected: { label: 'Rejected', labelAr: 'مرفوض', color: 'bg-red-600', icon: XCircle },
  implementing: { label: 'Implementing', labelAr: 'قيد التنفيذ', color: 'bg-violet-600', icon: Play },
  completed: { label: 'Completed', labelAr: 'مكتمل', color: 'bg-teal-600', icon: CheckCircle2 },
  closed: { label: 'Closed', labelAr: 'مغلق', color: 'bg-gray-600', icon: CheckCircle2 }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', labelAr: 'منخفض', color: 'bg-slate-500' },
  medium: { label: 'Medium', labelAr: 'متوسط', color: 'bg-blue-500' },
  high: { label: 'High', labelAr: 'عالي', color: 'bg-amber-500' },
  critical: { label: 'Critical', labelAr: 'حرج', color: 'bg-red-500' }
};

const RISK_CONFIG = {
  low: { label: 'Low', labelAr: 'منخفض', color: 'bg-emerald-500' },
  medium: { label: 'Medium', labelAr: 'متوسط', color: 'bg-amber-500' },
  high: { label: 'High', labelAr: 'عالي', color: 'bg-orange-500' },
  critical: { label: 'Critical', labelAr: 'حرج', color: 'bg-red-500' }
};

export default function ChangesPage() {
  const { t, i18n } = useTranslation();
  const { api, user } = useAuth();
  const { isRTL } = useLanguage();
  
  const [changes, setChanges] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showRiskDialog, setShowRiskDialog] = useState(false);
  const [showStepDialog, setShowStepDialog] = useState(false);
  const [selectedChange, setSelectedChange] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    category: 'operational',
    priority: 'medium',
    description: '',
    justification: '',
    target_date: '',
    affected_departments: '',
    rollback_plan: ''
  });
  
  const [approvalData, setApprovalData] = useState({
    decision: 'approved',
    comments: ''
  });
  
  const [riskData, setRiskData] = useState({
    hazard: '',
    risk_before: 'medium',
    mitigation: '',
    risk_after: 'low'
  });
  
  const [stepData, setStepData] = useState({
    step_number: 1,
    description: '',
    responsible: '',
    due_date: ''
  });

  const canManageChanges = ['admin', 'safety_manager', 'safety_officer'].includes(user?.role);
  const canApprove = ['admin', 'safety_manager'].includes(user?.role);

  useEffect(() => {
    if (api) {
      fetchData();
    }
  }, [api]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [changesRes, statsRes, usersRes] = await Promise.all([
        getChanges(api),
        getChangeStats(api),
        getUsers(api)
      ]);
      setChanges(changesRes.data);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      const data = {
        title: formData.title,
        title_ar: formData.title_ar || null,
        category: formData.category,
        priority: formData.priority,
        description: formData.description,
        justification: formData.justification,
        target_date: formData.target_date ? new Date(formData.target_date).toISOString() : null,
        affected_departments: formData.affected_departments ? formData.affected_departments.split(',').map(d => d.trim()) : [],
        rollback_plan: formData.rollback_plan || null
      };
      
      await createChange(api, data);
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to create change:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedChange) return;
    try {
      await submitChange(api, selectedChange.id);
      fetchData();
      const updated = await getChange(api, selectedChange.id);
      setSelectedChange(updated.data);
    } catch (error) {
      console.error('Failed to submit change:', error);
    }
  };

  const handleApprove = async () => {
    if (!selectedChange) return;
    try {
      setSaving(true);
      await approveChange(api, selectedChange.id, {
        decision: approvalData.decision,
        comments: approvalData.comments || undefined
      });
      setShowApprovalDialog(false);
      setApprovalData({ decision: 'approved', comments: '' });
      fetchData();
      const updated = await getChange(api, selectedChange.id);
      setSelectedChange(updated.data);
    } catch (error) {
      console.error('Failed to approve change:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleStartImplementation = async () => {
    if (!selectedChange) return;
    try {
      await startChangeImplementation(api, selectedChange.id);
      fetchData();
      const updated = await getChange(api, selectedChange.id);
      setSelectedChange(updated.data);
    } catch (error) {
      console.error('Failed to start implementation:', error);
    }
  };

  const handleComplete = async () => {
    if (!selectedChange) return;
    try {
      await completeChange(api, selectedChange.id);
      fetchData();
      const updated = await getChange(api, selectedChange.id);
      setSelectedChange(updated.data);
    } catch (error) {
      console.error('Failed to complete change:', error);
    }
  };

  const handleAddRisk = async () => {
    if (!selectedChange || !riskData.hazard || !riskData.mitigation) return;
    try {
      setSaving(true);
      await addChangeRiskAssessment(api, selectedChange.id, riskData);
      setShowRiskDialog(false);
      setRiskData({ hazard: '', risk_before: 'medium', mitigation: '', risk_after: 'low' });
      const updated = await getChange(api, selectedChange.id);
      setSelectedChange(updated.data);
    } catch (error) {
      console.error('Failed to add risk assessment:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddStep = async () => {
    if (!selectedChange || !stepData.description) return;
    try {
      setSaving(true);
      await addChangeImplementationStep(api, selectedChange.id, stepData);
      setShowStepDialog(false);
      setStepData({ step_number: (selectedChange.implementation_steps?.length || 0) + 1, description: '', responsible: '', due_date: '' });
      const updated = await getChange(api, selectedChange.id);
      setSelectedChange(updated.data);
    } catch (error) {
      console.error('Failed to add step:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStepStatus = async (stepId, newStatus) => {
    if (!selectedChange) return;
    try {
      await updateChangeImplementationStep(api, selectedChange.id, stepId, { status: newStatus });
      const updated = await getChange(api, selectedChange.id);
      setSelectedChange(updated.data);
    } catch (error) {
      console.error('Failed to update step:', error);
    }
  };

  const viewChange = async (change) => {
    try {
      const response = await getChange(api, change.id);
      setSelectedChange(response.data);
      setActiveTab('details');
      setShowViewDialog(true);
    } catch (error) {
      console.error('Failed to fetch change details:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      title_ar: '',
      category: 'operational',
      priority: 'medium',
      description: '',
      justification: '',
      target_date: '',
      affected_departments: '',
      rollback_plan: ''
    });
  };

  const filteredChanges = changes.filter(change => {
    const matchesSearch = change.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         change.change_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || change.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || change.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (category) => {
    const config = CHANGE_CATEGORIES.find(c => c.value === category);
    return i18n.language === 'ar' ? config?.labelAr : config?.label;
  };

  return (
    <div className="p-6 space-y-6" data-testid="changes-page">
      {/* Header */}
      <div className={cn("flex flex-col sm:flex-row justify-between gap-4", isRTL && "sm:flex-row-reverse")}>
        <div>
          <h1 className="text-2xl font-bold text-white font-barlow">
            {t('changes.title', 'Change Management')}
          </h1>
          <p className="text-slate-400 mt-1">
            {t('changes.subtitle', 'Manage operational and organizational changes')}
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateDialog(true)}
          className="bg-violet-600 hover:bg-violet-700"
          data-testid="new-change-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('changes.newRequest', 'New Change Request')}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
                <div className="p-2 bg-slate-700 rounded-lg">
                  <GitBranch className="w-5 h-5 text-slate-300" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                  <p className="text-xs text-slate-400">{t('changes.total', 'Total Changes')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
                <div className="p-2 bg-amber-900/50 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-400">{stats.pending_review}</p>
                  <p className="text-xs text-slate-400">{t('changes.pendingReview', 'Pending Review')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
                <div className="p-2 bg-violet-900/50 rounded-lg">
                  <Play className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-violet-400">{stats.in_progress}</p>
                  <p className="text-xs text-slate-400">{t('changes.inProgress', 'In Progress')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
                <div className="p-2 bg-emerald-900/50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-400">{stats.by_status?.completed || 0}</p>
                  <p className="text-xs text-slate-400">{t('common.completed', 'Completed')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
                <div className="p-2 bg-red-900/50 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-400">{stats.high_risk}</p>
                  <p className="text-xs text-slate-400">{t('changes.highRisk', 'High Risk')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className={cn("flex flex-col md:flex-row gap-4", isRTL && "md:flex-row-reverse")}>
            <div className="flex-1">
              <div className="relative">
                <Search className={cn("absolute top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400", isRTL ? "right-3" : "left-3")} />
                <Input
                  placeholder={t('common.search', 'Search...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn("bg-slate-900 border-slate-700 text-white", isRTL ? "pr-10" : "pl-10")}
                  data-testid="changes-search"
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px] bg-slate-900 border-slate-700 text-white" data-testid="status-filter">
                  <SelectValue placeholder={t('common.status', 'Status')} />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">{t('common.all', 'All')}</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {i18n.language === 'ar' ? config.labelAr : config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[160px] bg-slate-900 border-slate-700 text-white" data-testid="category-filter">
                  <SelectValue placeholder={t('changes.category', 'Category')} />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">{t('common.all', 'All')}</SelectItem>
                  {CHANGE_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {i18n.language === 'ar' ? cat.labelAr : cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Changes List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
        </div>
      ) : filteredChanges.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <GitBranch className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">{t('changes.noChanges', 'No change requests found')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredChanges.map(change => {
            const statusConfig = STATUS_CONFIG[change.status];
            const StatusIcon = statusConfig?.icon || FileText;
            const priorityConfig = PRIORITY_CONFIG[change.priority];
            const riskConfig = RISK_CONFIG[change.risk_level];
            
            return (
              <Card 
                key={change.id} 
                className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors cursor-pointer"
                onClick={() => viewChange(change)}
                data-testid={`change-card-${change.id}`}
              >
                <CardContent className="p-4">
                  <div className={cn("flex items-start justify-between gap-4", isRTL && "flex-row-reverse")}>
                    <div className="flex-1 space-y-2">
                      <div className={cn("flex items-center gap-2 flex-wrap", isRTL && "flex-row-reverse")}>
                        <Badge className={cn("text-xs", statusConfig?.color)}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {i18n.language === 'ar' ? statusConfig?.labelAr : statusConfig?.label}
                        </Badge>
                        <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">
                          {change.change_number}
                        </Badge>
                        <Badge className={cn("text-xs", priorityConfig?.color)}>
                          {i18n.language === 'ar' ? priorityConfig?.labelAr : priorityConfig?.label}
                        </Badge>
                        <Badge className={cn("text-xs", riskConfig?.color)}>
                          {i18n.language === 'ar' ? riskConfig?.labelAr : riskConfig?.label} Risk
                        </Badge>
                      </div>
                      
                      <h3 className="text-lg font-semibold text-white">
                        {i18n.language === 'ar' && change.title_ar ? change.title_ar : change.title}
                      </h3>
                      
                      <div className={cn("flex items-center gap-4 text-sm text-slate-400 flex-wrap", isRTL && "flex-row-reverse")}>
                        <span className={cn("flex items-center gap-1", isRTL && "flex-row-reverse")}>
                          <Filter className="w-4 h-4" />
                          {getCategoryLabel(change.category)}
                        </span>
                        <span className={cn("flex items-center gap-1", isRTL && "flex-row-reverse")}>
                          <User className="w-4 h-4" />
                          {change.requester_name}
                        </span>
                        <span className={cn("flex items-center gap-1", isRTL && "flex-row-reverse")}>
                          <Calendar className="w-4 h-4" />
                          {formatDate(change.created_at)}
                        </span>
                        {change.target_date && (
                          <span className={cn("flex items-center gap-1", isRTL && "flex-row-reverse")}>
                            <Clock className="w-4 h-4" />
                            Target: {formatDate(change.target_date)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <ChevronRight className={cn("w-5 h-5 text-slate-500", isRTL && "rotate-180")} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Change Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('changes.newRequest', 'New Change Request')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>{t('changes.changeTitle', 'Title')} *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder={t('changes.titlePlaceholder', 'e.g., Update runway inspection procedures')}
                  className="bg-slate-800 border-slate-700"
                  data-testid="change-title-input"
                />
              </div>
              
              <div>
                <Label>{t('changes.category', 'Category')} *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({...formData, category: value})}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="change-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {CHANGE_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {i18n.language === 'ar' ? cat.labelAr : cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>{t('changes.priority', 'Priority')} *</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value) => setFormData({...formData, priority: value})}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="change-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {i18n.language === 'ar' ? config.labelAr : config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>{t('changes.description', 'Description')} *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder={t('changes.descriptionPlaceholder', 'Describe the proposed change in detail...')}
                className="bg-slate-800 border-slate-700 min-h-[100px]"
                data-testid="change-description-input"
              />
            </div>
            
            <div>
              <Label>{t('changes.justification', 'Justification')} *</Label>
              <Textarea
                value={formData.justification}
                onChange={(e) => setFormData({...formData, justification: e.target.value})}
                placeholder={t('changes.justificationPlaceholder', 'Why is this change necessary?...')}
                className="bg-slate-800 border-slate-700 min-h-[80px]"
                data-testid="change-justification-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('changes.targetDate', 'Target Implementation Date')}</Label>
                <Input
                  type="date"
                  value={formData.target_date}
                  onChange={(e) => setFormData({...formData, target_date: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  data-testid="change-target-date"
                />
              </div>
              <div>
                <Label>{t('changes.affectedDepartments', 'Affected Departments')}</Label>
                <Input
                  value={formData.affected_departments}
                  onChange={(e) => setFormData({...formData, affected_departments: e.target.value})}
                  placeholder="Operations, Maintenance, Safety..."
                  className="bg-slate-800 border-slate-700"
                  data-testid="change-departments"
                />
              </div>
            </div>
            
            <div>
              <Label>{t('changes.rollbackPlan', 'Rollback Plan')}</Label>
              <Textarea
                value={formData.rollback_plan}
                onChange={(e) => setFormData({...formData, rollback_plan: e.target.value})}
                placeholder={t('changes.rollbackPlaceholder', 'How to revert if the change fails...')}
                className="bg-slate-800 border-slate-700 min-h-[60px]"
                data-testid="change-rollback"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-slate-600">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={!formData.title || !formData.description || !formData.justification || saving}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="create-change-submit"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('changes.createDraft', 'Create Draft')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Change Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <div className={cn("flex items-center justify-between", isRTL && "flex-row-reverse")}>
              <DialogTitle className="text-xl">
                {i18n.language === 'ar' && selectedChange?.title_ar ? selectedChange.title_ar : selectedChange?.title}
              </DialogTitle>
              {selectedChange && (
                <Badge className={STATUS_CONFIG[selectedChange.status]?.color}>
                  {i18n.language === 'ar' 
                    ? STATUS_CONFIG[selectedChange.status]?.labelAr 
                    : STATUS_CONFIG[selectedChange.status]?.label}
                </Badge>
              )}
            </div>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-slate-800 w-full">
              <TabsTrigger value="details" className="flex-1 data-[state=active]:bg-slate-700">
                <Eye className="w-4 h-4 mr-2" />
                {t('common.details', 'Details')}
              </TabsTrigger>
              <TabsTrigger value="risk" className="flex-1 data-[state=active]:bg-slate-700">
                <Shield className="w-4 h-4 mr-2" />
                {t('changes.riskAssessment', 'Risk')}
              </TabsTrigger>
              <TabsTrigger value="implementation" className="flex-1 data-[state=active]:bg-slate-700">
                <ListTodo className="w-4 h-4 mr-2" />
                {t('changes.implementation', 'Implementation')}
              </TabsTrigger>
              <TabsTrigger value="approvals" className="flex-1 data-[state=active]:bg-slate-700">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {t('changes.approvals', 'Approvals')}
              </TabsTrigger>
            </TabsList>
            
            <ScrollArea className="h-[400px] mt-4">
              <TabsContent value="details" className="space-y-4 m-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">{t('changes.changeNumber', 'Change Number')}</p>
                    <p className="text-white font-medium">{selectedChange?.change_number}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">{t('changes.category', 'Category')}</p>
                    <p className="text-white">{getCategoryLabel(selectedChange?.category)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">{t('changes.priority', 'Priority')}</p>
                    <Badge className={PRIORITY_CONFIG[selectedChange?.priority]?.color}>
                      {i18n.language === 'ar' 
                        ? PRIORITY_CONFIG[selectedChange?.priority]?.labelAr 
                        : PRIORITY_CONFIG[selectedChange?.priority]?.label}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">{t('changes.riskLevel', 'Risk Level')}</p>
                    <Badge className={RISK_CONFIG[selectedChange?.risk_level]?.color}>
                      {i18n.language === 'ar' 
                        ? RISK_CONFIG[selectedChange?.risk_level]?.labelAr 
                        : RISK_CONFIG[selectedChange?.risk_level]?.label}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">{t('changes.requester', 'Requester')}</p>
                    <p className="text-white">{selectedChange?.requester_name}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">{t('changes.targetDate', 'Target Date')}</p>
                    <p className="text-white">{formatDate(selectedChange?.target_date)}</p>
                  </div>
                </div>
                
                <div className="space-y-1 pt-4 border-t border-slate-700">
                  <p className="text-xs text-slate-500">{t('changes.description', 'Description')}</p>
                  <p className="text-slate-300">{selectedChange?.description}</p>
                </div>
                
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">{t('changes.justification', 'Justification')}</p>
                  <p className="text-slate-300">{selectedChange?.justification}</p>
                </div>
                
                {selectedChange?.rollback_plan && (
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">{t('changes.rollbackPlan', 'Rollback Plan')}</p>
                    <p className="text-slate-300">{selectedChange.rollback_plan}</p>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="pt-4 border-t border-slate-700 flex flex-wrap gap-2">
                  {selectedChange?.status === 'draft' && (
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={handleSubmit}
                      data-testid="submit-change-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {t('changes.submitForReview', 'Submit for Review')}
                    </Button>
                  )}
                  {canApprove && ['under_review', 'risk_assessment'].includes(selectedChange?.status) && (
                    <Button 
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setShowApprovalDialog(true)}
                      data-testid="approve-change-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {t('changes.reviewDecision', 'Review Decision')}
                    </Button>
                  )}
                  {canManageChanges && selectedChange?.status === 'approved' && (
                    <Button 
                      className="bg-violet-600 hover:bg-violet-700"
                      onClick={handleStartImplementation}
                      data-testid="start-implementation-btn"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {t('changes.startImplementation', 'Start Implementation')}
                    </Button>
                  )}
                  {canManageChanges && selectedChange?.status === 'implementing' && (
                    <Button 
                      className="bg-teal-600 hover:bg-teal-700"
                      onClick={handleComplete}
                      data-testid="complete-change-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {t('changes.markComplete', 'Mark Complete')}
                    </Button>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="risk" className="space-y-4 m-0">
                {canManageChanges && !['completed', 'closed', 'rejected'].includes(selectedChange?.status) && (
                  <Button 
                    onClick={() => setShowRiskDialog(true)}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="add-risk-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('changes.addRiskAssessment', 'Add Risk Assessment')}
                  </Button>
                )}
                
                {selectedChange?.risk_assessments?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedChange.risk_assessments.map((risk, idx) => (
                      <div key={risk.id} className="p-4 bg-slate-800 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-white font-medium">{risk.hazard}</p>
                            <p className="text-slate-400 text-sm mt-1">{risk.mitigation}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm">
                              <span>
                                Before: <Badge className={RISK_CONFIG[risk.risk_before]?.color}>{risk.risk_before}</Badge>
                              </span>
                              <span>→</span>
                              <span>
                                After: <Badge className={RISK_CONFIG[risk.risk_after]?.color}>{risk.risk_after}</Badge>
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-2">
                              Assessed by {risk.assessed_by} on {formatDate(risk.assessed_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>{t('changes.noRiskAssessments', 'No risk assessments recorded')}</p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="implementation" className="space-y-4 m-0">
                {canManageChanges && ['approved', 'implementing'].includes(selectedChange?.status) && (
                  <Button 
                    onClick={() => {
                      setStepData({
                        ...stepData,
                        step_number: (selectedChange?.implementation_steps?.length || 0) + 1
                      });
                      setShowStepDialog(true);
                    }}
                    className="bg-violet-600 hover:bg-violet-700"
                    data-testid="add-step-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('changes.addStep', 'Add Implementation Step')}
                  </Button>
                )}
                
                {selectedChange?.implementation_steps?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedChange.implementation_steps.sort((a, b) => a.step_number - b.step_number).map((step) => (
                      <div key={step.id} className={cn(
                        "p-4 bg-slate-800 rounded-lg border-l-4",
                        step.status === 'completed' ? "border-emerald-500" : 
                        step.status === 'in_progress' ? "border-amber-500" : "border-slate-600"
                      )}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-violet-400 font-mono">Step {step.step_number}</span>
                              <Badge className={
                                step.status === 'completed' ? "bg-emerald-600" :
                                step.status === 'in_progress' ? "bg-amber-600" : "bg-slate-600"
                              }>
                                {step.status.replace('_', ' ')}
                              </Badge>
                            </div>
                            <p className="text-white mt-1">{step.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                              {step.responsible_name && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {step.responsible_name}
                                </span>
                              )}
                              {step.due_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Due: {formatDate(step.due_date)}
                                </span>
                              )}
                            </div>
                          </div>
                          {canManageChanges && step.status !== 'completed' && selectedChange?.status === 'implementing' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-emerald-600 text-emerald-400"
                              onClick={() => handleUpdateStepStatus(step.id, 'completed')}
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <ListTodo className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>{t('changes.noSteps', 'No implementation steps defined')}</p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="approvals" className="space-y-4 m-0">
                {selectedChange?.approvals?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedChange.approvals.map((approval) => (
                      <div key={approval.id} className={cn(
                        "p-4 bg-slate-800 rounded-lg border-l-4",
                        approval.decision === 'approved' ? "border-emerald-500" : 
                        approval.decision === 'rejected' ? "border-red-500" : "border-amber-500"
                      )}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">{approval.approver_name}</span>
                              <Badge className={
                                approval.decision === 'approved' ? "bg-emerald-600" :
                                approval.decision === 'rejected' ? "bg-red-600" : "bg-amber-600"
                              }>
                                {approval.decision}
                              </Badge>
                            </div>
                            <p className="text-slate-500 text-sm">{approval.role}</p>
                            {approval.comments && (
                              <p className="text-slate-300 mt-2">{approval.comments}</p>
                            )}
                            <p className="text-xs text-slate-500 mt-2">{formatDate(approval.approved_at)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>{t('changes.noApprovals', 'No approvals recorded yet')}</p>
                  </div>
                )}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{t('changes.reviewDecision', 'Review Decision')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('changes.decision', 'Decision')} *</Label>
              <Select value={approvalData.decision} onValueChange={(v) => setApprovalData({...approvalData, decision: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="approved">Approve</SelectItem>
                  <SelectItem value="rejected">Reject</SelectItem>
                  <SelectItem value="conditional">Conditional Approval</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{t('changes.comments', 'Comments')}</Label>
              <Textarea
                value={approvalData.comments}
                onChange={(e) => setApprovalData({...approvalData, comments: e.target.value})}
                placeholder="Add your comments..."
                className="bg-slate-800 border-slate-700 min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprovalDialog(false)} className="border-slate-600">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleApprove}
              disabled={saving}
              className={approvalData.decision === 'approved' ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('changes.submitDecision', 'Submit Decision')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Risk Assessment Dialog */}
      <Dialog open={showRiskDialog} onOpenChange={setShowRiskDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{t('changes.addRiskAssessment', 'Add Risk Assessment')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('changes.hazard', 'Hazard')} *</Label>
              <Textarea
                value={riskData.hazard}
                onChange={(e) => setRiskData({...riskData, hazard: e.target.value})}
                placeholder="Describe the potential hazard..."
                className="bg-slate-800 border-slate-700"
              />
            </div>
            
            <div>
              <Label>{t('changes.riskBefore', 'Risk Before Mitigation')}</Label>
              <Select value={riskData.risk_before} onValueChange={(v) => setRiskData({...riskData, risk_before: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {Object.entries(RISK_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{t('changes.mitigation', 'Mitigation Measures')} *</Label>
              <Textarea
                value={riskData.mitigation}
                onChange={(e) => setRiskData({...riskData, mitigation: e.target.value})}
                placeholder="Describe mitigation measures..."
                className="bg-slate-800 border-slate-700"
              />
            </div>
            
            <div>
              <Label>{t('changes.riskAfter', 'Risk After Mitigation')}</Label>
              <Select value={riskData.risk_after} onValueChange={(v) => setRiskData({...riskData, risk_after: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {Object.entries(RISK_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRiskDialog(false)} className="border-slate-600">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleAddRisk}
              disabled={!riskData.hazard || !riskData.mitigation || saving}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('changes.addAssessment', 'Add Assessment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Implementation Step Dialog */}
      <Dialog open={showStepDialog} onOpenChange={setShowStepDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{t('changes.addStep', 'Add Implementation Step')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('changes.stepNumber', 'Step Number')}</Label>
                <Input
                  type="number"
                  value={stepData.step_number}
                  onChange={(e) => setStepData({...stepData, step_number: parseInt(e.target.value)})}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <Label>{t('changes.dueDate', 'Due Date')}</Label>
                <Input
                  type="date"
                  value={stepData.due_date}
                  onChange={(e) => setStepData({...stepData, due_date: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>
            
            <div>
              <Label>{t('changes.stepDescription', 'Description')} *</Label>
              <Textarea
                value={stepData.description}
                onChange={(e) => setStepData({...stepData, description: e.target.value})}
                placeholder="Describe this implementation step..."
                className="bg-slate-800 border-slate-700"
              />
            </div>
            
            <div>
              <Label>{t('changes.responsible', 'Responsible Person')}</Label>
              <Select value={stepData.responsible} onValueChange={(v) => setStepData({...stepData, responsible: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Select assignee..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStepDialog(false)} className="border-slate-600">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleAddStep}
              disabled={!stepData.description || saving}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('changes.addStep', 'Add Step')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
